import React from "react";
import { Fab, Tooltip } from "@mui/material";
import KeyboardArrowUpRoundedIcon from "@mui/icons-material/KeyboardArrowUpRounded";
import { ACCENT, surface, text } from "../theme";
import { EmbedBridge } from "../hooks/useEmbedBridge";

/** How far down before the button is worth offering. */
const SHOW_AFTER_PX = 700;
/** Gap from the bottom-right corner of the viewport. */
const EDGE_GAP = 26;

interface Props {
  bridge: EmbedBridge;
}

/**
 * Back-to-top control, for the standalone reader only.
 *
 * Embedded, the host page supplies its own — thetezos.com has an Elementor
 * scroll-to-top element — so rendering one here would stack a second button in
 * the same corner. The host also owns the scroll position in that case: the
 * iframe is sized to the reader's full height and never scrolls itself, so
 * there is nothing here for a button to scroll.
 *
 * Rendered without an entrance animation on purpose: MUI's Zoom starts the
 * button at scale 0, and a transition that does not advance would leave a
 * mounted-but-invisible 0x0 button.
 */
export function ScrollTopButton({ bridge }: Props) {
  const { embedded, visibleTop, scrollToTop } = bridge;

  if (embedded) return null;
  if (visibleTop <= SHOW_AFTER_PX) return null;

  return (
    <Tooltip title="Back to top" placement="left">
      <Fab
        size="medium"
        aria-label="Back to top"
        onClick={scrollToTop}
        sx={{
          position: "fixed",
          right: EDGE_GAP,
          bottom: EDGE_GAP,
          zIndex: 30,
          backgroundColor: surface.raised,
          color: text.secondary,
          border: `1px solid ${surface.borderStrong}`,
          "&:hover": {
            backgroundColor: surface.cardHover,
            color: ACCENT,
            borderColor: ACCENT,
          },
        }}
      >
        <KeyboardArrowUpRoundedIcon />
      </Fab>
    </Tooltip>
  );
}
